import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { loginSchema, type LoginInput } from '@lightbm/shared';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Notice } from '@/components/ui/Notice';
import { useErrorText } from '@/i18n/messages';
import { useAuth } from '../auth-context';
import { usePublicContext } from '../components/AuthLayout';
import { PasswordInput } from '../components/PasswordInput';
import { safeNextPath } from '../guards';

export function SignInPage() {
  const { t } = useTranslation('auth');
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const errorText = useErrorText();
  const { data: context } = usePublicContext();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signIn(values);
      void navigate(safeNextPath(params.get('next')), { replace: true });
    } catch (error) {
      setFormError(errorText(error));
    }
  });

  const reason = params.get('reason');

  return (
    <>
      <h1 className="text-[34px] leading-tight font-semibold tracking-tight">
        {t('signIn.title')}
      </h1>
      <p className="mt-2 text-[17px] text-ink-2">{t('signIn.subtitle')}</p>

      {reason === 'expired' && (
        <Notice tone="info" className="mt-6" role="status">
          {t('signIn.sessionEnded', { minutes: context?.security.sessionIdleMinutes ?? 30 })}
        </Notice>
      )}
      {reason === 'restored' && (
        <Notice tone="info" className="mt-6" role="status">
          {t('signIn.restored')}
        </Notice>
      )}
      {reason === 'password-reset' && (
        <Notice tone="success" className="mt-6" role="status">
          {t('signIn.passwordReset')}
        </Notice>
      )}

      <form
        onSubmit={onSubmit}
        noValidate
        className="mt-8 rounded-[16px] border border-line bg-surface p-6 shadow-card sm:p-8"
      >
        <div className="space-y-5">
          <Field label={t('fields.username')} error={errors.username}>
            <Input
              {...register('username')}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={t('fields.usernamePlaceholder')}
              className="h-13 text-[17px]"
            />
          </Field>
          <Field
            label={t('fields.password')}
            error={errors.password}
            labelAction={
              <Link
                to="/forgot-password"
                className="font-medium text-primary underline underline-offset-2"
              >
                {t('signIn.forgot')}
              </Link>
            }
          >
            <PasswordInput
              {...register('password')}
              autoComplete="current-password"
              className="h-13 pr-12 text-[17px]"
            />
          </Field>

          {formError && (
            <Notice tone="danger" role="alert">
              {formError}
            </Notice>
          )}

          <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
            {t('signIn.submit')}
          </Button>
        </div>

        {context && (
          <p className="mt-6 flex gap-3 text-ink-2">
            <Lock className="mt-0.5 size-5 shrink-0" strokeWidth={1.75} aria-hidden="true" />
            <span>
              {t('signIn.securityNote', {
                lockout: context.security.lockoutMinutes,
                attempts: context.security.maxFailedAttempts,
                idle: context.security.sessionIdleMinutes,
              })}
            </span>
          </p>
        )}
      </form>

      <p className="mt-8 text-ink-2">{t('signIn.noAccount')}</p>
    </>
  );
}
